// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { gridGapStyles, siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    textColumnOne = "textColumnOne",
    textColumnTwo = "textColumnTwo",
    variant = "variant",
}

interface BlogArticleHeaderProps extends WidgetProps {
    fields: {
        [fields.textColumnOne]: string;
        [fields.textColumnTwo]: string;
        [fields.variant]: string;
    };
}

export interface BlogArticleHeaderStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    infosWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    infos?: InjectableCss;
    publishDate?: InjectableCss;
    author?: TypographyPresentationProps;
    textWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const blogArticleHeaderStyles: BlogArticleHeaderStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
        `,
    },
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            ${x => breakpointValues(x.theme.design.layout.spacing.content, "padding-top")}
            ${breakpointValues(pxToRemArray([25, 32, null, null, null]), "padding-bottom")}
        `,
    },
    infosWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(24), "padding")};
        `,
    },
    title: {
        variant: "h1",
        css: css`
            ${spacingStyles(null, pxToRem(16))};
        `,
    },
    infos: {
        css: css`
            display: flex;
            flex-direction: column;
            column-gap: ${pxToRem(16)};
            flex-wrap: wrap;
            font-size: ${x => x.theme.design.typography.size.smaller};
            color: ${getColor("text.accent")};

            ${breakpoint("sm")} {
                flex-direction: row;
            }
        `,
    },
    textWrapper: {
        css: css`
            padding-top: ${pxToRem(32)};
            color: ${getColor("text.accent")};
            border-top: 1px solid ${getColor("common.borderDark")};
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([30]))}
        `,
    },
    gridItem: { width: [12, 12, 6, 6, 6] },
};

const styles = blogArticleHeaderStyles;

const BlogArticleHeader: FC<BlogArticleHeaderProps> = ({ fields }) => {
    const { textColumnOne, textColumnTwo, variant } = fields;
    const currentPage = useCurrentPage();
    const language = useCurrentLanguage();

    if (!currentPage) {
        return null;
    }

    const {
        fields: { author, publishDate, title },
    } = currentPage;

    const publishDateDisplay = getLocalizedDateTime({
        dateTime: new Date(publishDate),
        language,
        options: constants.blog.getLocalizedDateTimeOptions,
    });

    const hasOneColumnText = Boolean(variant === "one" && textColumnOne);
    const hasTwoColumnsText = Boolean(variant === "two" && (textColumnOne || textColumnTwo));

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.infosWrapper}>
                    <Typography {...styles.title}>{title}</Typography>
                    <StyledWrapper {...styles.infos}>
                        {publishDate ? (
                            <StyledWrapper {...styles.publishDate} as="time" dateTime={publishDate.toString()}>
                                {translate("Published on: {0}", publishDateDisplay)}
                            </StyledWrapper>
                        ) : null}
                        {author ? <Typography {...styles.author}>{translate("Author: {0}", author)}</Typography> : null}
                    </StyledWrapper>
                </StyledWrapper>
                {hasTwoColumnsText ? (
                    <StyledWrapper {...styles.textWrapper}>
                        <GridContainer {...styles.gridContainer}>
                            <GridItem {...styles.gridItem}>{parse(textColumnOne, parserOptions)}</GridItem>
                            <GridItem {...styles.gridItem}>{parse(textColumnTwo, parserOptions)}</GridItem>
                        </GridContainer>
                    </StyledWrapper>
                ) : hasOneColumnText ? (
                    <StyledWrapper {...styles.textWrapper}>{parse(textColumnOne, parserOptions)}</StyledWrapper>
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BlogArticleHeader,
    definition: {
        group: constants.widgetGroup.blog,
        displayName: "Article Header",
        allowedContexts: [constants.pageType.blogArticlePage],
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Number of text columns",
                fieldType: "Contextual",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "" },
                    { displayName: "One", value: "one" },
                    { displayName: "Two", value: "two" },
                ],
                defaultValue: "one",
                hideEmptyOption: true,
            },
            {
                name: fields.textColumnOne,
                displayName: "Text",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: x => x.fields.variant === "one" || x.fields.variant === "two",
            },
            {
                name: fields.textColumnTwo,
                displayName: "Text",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: x => x.fields.variant === "two",
            },
        ],
    },
};

export default widgetModule;
